<template>
  <div>
    <b-row align-h="between">
      <b-col :cols="6" class="mb-3 mb-md-0">
        <h3 class="headings">
          {{ $t('buyers.single.summary.fields.details') }}
        </h3>
      </b-col>
      <b-col :cols="6" class="text-right">
        <span id="disabled-button-wrapper">
          <b-button
            class="mt-4 mr-4"
            :disabled="ddpCheck || !hasPermission || clientUnlocked"
            variant="primary"
            @click="navigateToInvoice()"
            >{{ $t('dashboard.addInvoiceBtn') }}</b-button
          >
        </span>
        <b-tooltip
          v-if="!hasPermission"
          target="disabled-button-wrapper"
          triggers="hover"
          >{{ $t('invoices.esign.toolTip') }}</b-tooltip
        >
      </b-col>
    </b-row>
    <div class="section">
      <b-row>
        <b-col :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.summary.fields.name') }}
          </div>
          <div class="subtitle">{{ buyer.importer }}</div>
        </b-col>
        <b-col :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.summary.fields.status') }}
          </div>
          <div class="subtitle">
            <span
              class="buyers-stage-dot"
              :class="getBuyerStageCategory(buyer.new_portal_status)"
            ></span>
            <!-- {{ buyer.new_portal_status }} -->
            {{ $t(`buyers.index.status['${buyer.new_portal_status}']`) }}
          </div>
          <span class="product-limit"
            >{{ $t('buyers.single.summary.fields.productLimit') }}
            {{ buyer.product_based_limit | currency(null, null) }}</span
          >
        </b-col>
      </b-row>
    </div>
    <h3 class="headings">{{ $t('buyers.index.limits') }}</h3>
    <div class="section">
      <buyer-limit :buyer="buyer" />
    </div>
    <h3 class="headings">{{ $t('buyers.single.product.title') }}</h3>
    <div class="section">
      <b-row>
        <b-col :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.product.fields.product') }}
          </div>
          <div class="subtitle">{{ buyer.plan.product_value }}</div>
        </b-col>
        <b-col :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.product.fields.paymentTenor') }}
          </div>
          <div class="subtitle">
            {{ buyer.plan.payment_tenor }}
            {{ $t('buyers.single.buyer.product.daysFrom') }}
            {{ buyer.plan.term_from_value }}
          </div>
        </b-col>
        <b-col :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.product.fields.currency') }}
          </div>
          <div class="subtitle">{{ buyer.plan.currency_value }}</div>
        </b-col>
        <b-col :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.product.fields.cheque') }}
          </div>
          <div class="subtitle">{{ buyer.plan.cheque_value }}</div>
        </b-col>
      </b-row>
    </div>
    <h3 class="headings">{{ $t('buyers.single.pricing.title') }}</h3>
    <div class="section pb-5">
      <b-row>
        <b-col :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.pricing.fields.advanceRate') }}
          </div>
          <div class="subtitle">
            {{ buyer.plan.advance_rate | percentSign }}
            {{ $t('buyers.single.pricing.fields.invoiceValue') }}
          </div>
        </b-col>
        <b-col :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.pricing.fields.factoringComission') }}
          </div>
          <div
            v-for="factoringComission in buyer.plan.factoring_commission"
            :key="factoringComission.days"
            class="subtitle"
          >
            {{ $t('buyers.single.buyer.plan.upto') }}
            {{ factoringComission.days }}
            {{ $t('buyers.single.buyer.plan.days') }}
            {{ factoringComission.rate | percentSign }}
            {{ $t('buyers.single.buyer.plan.ofNetInvoiceValue') }}
          </div>
        </b-col>
        <b-col :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.pricing.fields.interestRate') }}
          </div>
          <div class="subtitle">
            {{ buyer.plan.interest_rate | percentSign }}
            {{ $t('buyers.single.buyer.plan.pa') }}
            {{ $t('buyers.single.pricing.fields.calculation') }}
          </div>
        </b-col>
      </b-row>
    </div>
    <h3 class="headings">{{ $t('buyers.single.banking.title') }}</h3>
    <h3 class="title bank-desc font-weight-normal">
      {{ $t('buyers.single.banking.desc') }}
    </h3>
    <div class="bank-section pb-5">
      <b-row>
        <b-col
          v-if="bankInfo.bank_name"
          :md="3"
          :cols="12"
          class="mb-3 mb-md-0"
        >
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.banking.fields.bankName') }}
          </div>
          <div class="subtitle">{{ bankInfo.bank_name }}</div>
        </b-col>
        <b-col
          v-if="bankInfo.bank_address"
          :md="3"
          :cols="12"
          class="mb-3 mb-md-0"
        >
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.banking.fields.bankAddress') }}
          </div>
          <div v-if="bankAddressType" class="subtitle">
            {{ bankInfo.bank_address }}
          </div>
          <div
            v-else
            class="subtitle"
            v-for="address in bankInfo.bank_address"
            :key="address"
          >
            {{ address }}
          </div>
        </b-col>
        <b-col
          v-if="bankInfo.aba_routing"
          :md="3"
          :cols="12"
          class="mb-3 mb-md-0"
        >
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.banking.fields.aba') }}
          </div>
          <div class="subtitle">{{ bankInfo.aba_routing }}</div>
        </b-col>
        <b-col v-if="bankInfo.ac_name" :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.banking.fields.accName') }}
          </div>
          <div class="subtitle">{{ bankInfo.ac_name }}</div>
        </b-col>
      </b-row>
    </div>
    <div class="pb-5 bank-section">
      <b-row>
        <b-col v-if="bankInfo.ac_no" :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.banking.fields.accNo') }}
          </div>
          <div class="subtitle">{{ bankInfo.ac_no }}</div>
        </b-col>
        <b-col v-if="bankInfo.swift" :md="3" :cols="12" class="mb-3 mb-md-0">
          <div class="title pb-0 pb-md-1">
            {{ $t('buyers.single.banking.fields.swift') }}
          </div>
          <div class="subtitle">{{ bankInfo.swift }}</div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import BuyerLimit from '@/components/buyer/BuyerLimit/'
import buyersService from '@/services/buyers'
import monitoringService from '@/services/monitoring'

export default {
  data() {
    return {
      bankInfo: {},
      monitoringData: null
    }
  },
  methods: {
    getBuyerStageCategory(buyerStatus) {
      if (!buyerStatus) {
        return ''
      }
      return buyerStatus.toLowerCase().split(' ').join('-')
    },
    navigateToInvoice() {
      if (this.newInvoiceRedirect) {
        this.$mpTrack('Buyer - Redirected to accounts page', {
          revoke_date: this.monitoringData?.revoke_date
        })
        this.$router.push({
          name: 'account'
        })
      } else {
        this.$router.push({
          name: 'invoicesAdd',
          query: { buyerId: this.buyer.id }
        })
      }
    },
    fetchBankDetails() {
      return buyersService
        .fetchBuyersBankDetails(this.buyer.id, {
          vars: {
            currency: this.buyer.currency
          }
        })
        .then((res) => {
          this.bankInfo = res.data
        })
    },
    async fetchMonitoringData() {
      this.loading = true
      try {
        const res = await monitoringService.getMonitoringData(
          this.$store.getters.accountId
        )
        this.monitoringData = res.data
      } catch (error) {
        console.error('Error fetching monitoring data:', error)
        // Optionally set an error message to display to the user
        this.errorMessage = 'Failed to fetch monitoring data. Please try again.'
      } finally {
        this.loading = false // Ensure loading is set to false regardless of success or error
      }
    }
  },
  computed: {
    hasPermission() {
      return this.$store.getters.hasInvoiceAddAndCreatePermission()
      /**
       * ?: below is the old code for access roles
        return (
          this.$store.getters.hasPermission('esign') ||
           this.$store.getters.hasPermission('edit')
        )
       **/
    },
    ddpCheck() {
      return this.$store.getters.ddpCheck === 0
    },
    clientUnlocked() {
      return !this.buyer['locked?']
    },
    bankAddressType() {
      return typeof this.bankInfo.bank_address === 'string'
    },
    newInvoiceRedirect() {
      if (
        !!this.monitoringData &&
        !!this.monitoringData.mail_date &&
        this.$moment(this.monitoringData.revoke_date).isValid() &&
        this.$moment(this.monitoringData.mail_date).isValid() &&
        (!this.monitoringData.financials.active ||
          !this.monitoringData.gst.active)
      ) {
        const mailDate = new Date(this.monitoringData.mail_date) // Convert to Date object
        const currentDate = new Date() // Get today's date

        // Calculate the date 45 days after the mail_date
        const days60AfterMailDate = new Date(mailDate)
        days60AfterMailDate.setDate(mailDate.getDate() + 60)

        return currentDate >= new Date(this.monitoringData.revoke_date)
      }
      return false
    }
  },
  props: ['buyer'],
  mounted() {
    this.fetchMonitoringData()
    this.fetchBankDetails()
  },
  components: {
    BuyerLimit
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.product-limit {
  font-size: 12px;
  font-style: italic;
}
.section {
  padding: 1rem 2rem 0.5rem;
  border-bottom: 1px solid $drip-border-color;
}
.bank-section {
  padding: 1rem 2rem 0.5rem;
}
.bank-desc {
  padding-left: 2rem;
  text-transform: none;
  font-family: $montserrat;
}
.buyers-stage-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 100%;
  margin-right: 8px;
  &.processing {
    background: $processing-color;
  }
  &.on-hold {
    background: $pending-color;
  }
  &.rejected {
    background: $advanced-color;
  }
  &.funding-available {
    background: $approved-color;
  }
}
</style>
